"use strict";

/*
 * CPM Auth NPM Package
 * Copyright (C) 2023 CPM
 *
 * All rights reserved.
 *
 * This software and its documentation are considered to be the Confidential and Proprietary Information of
 * CPM. Any use, copying, reproduction, modification, distribution, or disclosure directly
 * or indirectly, in whole or in part, including in derivative works, is prohibited without the express written
 * consent of CPM.
 */
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.acquireCookieLock = exports.waitForUnlock = exports.clearLock = exports.claimLock = exports.peekLock = exports.parseCookieString = exports.getTabId = exports.TAB_ID = exports.LockTakenError = void 0;
//
//  Private Functions
//
/**
 * Generates a random alphanumeric string of a given length.
 * @param {number} Length of the string to generate.
 * @remarks Used to generate a "Tab ID" for each Browser tab.
 * @returns {string} Random string consisting of uppercase and lowercase letters and digits.
 */
const generateRandomString = length => {
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let randomString = "";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    randomString += characters.charAt(randomIndex);
  }
  return randomString;
};
/**
 * Waits a random amount of time between 0 and 1000 milliseconds.
 * @remarks Used to wait a variable amount of time
 * @param {number} Length of the string to generate.
 * @returns {string} Random string consisting of uppercase and lowercase letters and digits.
 */
const wait100 = () => {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve();
    }, 100);
  });
};
/**
 * Sleeps the specified number of milliseconds.
 * @param {number} Number of milliseconds to sleep.
 */
const wait = ms => {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve();
    }, ms);
  });
};
//
//  Public Functions
//
//  Define Errors
class LockTakenError extends Error {}
exports.LockTakenError = LockTakenError;
//  Define Constants
exports.TAB_ID = generateRandomString(10);
const getTabId = () => {
  return exports.TAB_ID;
};
exports.getTabId = getTabId;
/**
 * Generates a string suitable for cookie headers from an object.
 * @param {Object} obj - The object to encode into a cookie string.
 * @returns {string} - The encoded cookie string.
 */
function objectToCookieString(obj) {
  let cookieString = Object.entries(obj).map(_ref => {
    let [key, value] = _ref;
    return `${key}=${value}`;
  }).join('; ');
  cookieString += '; Secure';
  return cookieString;
}
/**
 * Parses a cookie string into an object.
 * @param {string} cookieString - The string from document.cookie.
 * @returns {Object} - The parsed cookie object.
 */
const parseCookieString = cookieString => {
  const obj = {};
  cookieString.split(';').forEach(cookie => {
    const [key, value] = cookie.split('=').map(part => part.trim());
    obj[key] = value;
  });
  return obj;
};
exports.parseCookieString = parseCookieString;
/**
 * Retrieves the current "claim" set for the cookie lock.
 * @param {string} lockKey - The key for the lock.
 * @returns {LockClaim | null} - Current lock claim or null.
 */
const peekLock = lockKey => {
  try {
    // console.log("Attempting to Parse Cookie String: " + document.cookie);
    const cookies = (0, exports.parseCookieString)(document.cookie);
    // console.log("Parsed Cookies: " + JSON.stringify(cookies));
    const currValue = cookies[lockKey] ? cookies[lockKey] : null;
    return currValue;
  } catch (err) {
    //  NOTE:  On parse failure we assume it's null, because otherwise we'll get stuck in an infinite loop for ALL apps.
    console.error(`Failed to parse the '${lockKey}' cookie lock.  Continuing as if the cookie were null.`);
    return null;
  }
};
exports.peekLock = peekLock;
/**
 * Tentatively claim the Cookie Lock by using the given Lock Claim.
 * The lock will expire in 5 seconds, allowing others to attempt to acquire it if this lock is not released.
 * @param {string} lockKey - The key for the lock.
 * @param {string} payload - The payload for the lock claim.
 */
const claimLock = (lockKey, lockClaim) => {
  console.log(`Claiming Lock with Lock Key: '${lockKey}' and Lock Claim: '${lockClaim}'`);
  const expires = new Date();
  expires.setTime(expires.getTime() + 5 * 1000); // Set expiration to 5 seconds from now
  const cookieValue = lockClaim;
  const cookiePayload = objectToCookieString({
    [lockKey]: cookieValue,
    'expires': expires.toUTCString(),
    'path': '/',
    'domain': '.cpm.org',
    'SameSite': 'None'
  });
  document.cookie = cookiePayload;
  console.log(`Added Cookie with Value: '${cookiePayload}'`);
};
exports.claimLock = claimLock;
/**
 * Clears the Cookie Lock.
 * @param {string} lockKey - The key for the lock to clear.
 */
const clearLock = lockKey => {
  document.cookie = objectToCookieString({
    [lockKey]: '',
    'expires': 'Thu, 01 Jan 1970 00:00:00 GMT',
    'path': '/',
    'domain': '.cpm.org'
  });
};
exports.clearLock = clearLock;
/**
 * Waits until the given lock is released.
 * @param lockKey
 * TODO-WILL:  Consider using event signaling (interrupts) for this on clearLock instead of a poll.
 */
const waitForUnlock = async lockKey => {
  while (!!(0, exports.peekLock)(lockKey)) {
    await wait(500);
  }
};
exports.waitForUnlock = waitForUnlock;
/**
 * Blocks until the cookie lock is either acquired or fails to be acquired.
 * @param {string} lockKey - A unique identifier for this cookie lock.
 * @param {IAcquireCookieLockOptions} options - Options for the cookie lock acquisition.
 */
const acquireCookieLock = async function (lockKey) {
  let {
    lockClaim = (0, exports.getTabId)(),
    hold = false
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  console.log(`Attempting to Acquire the Cookie Lock with Lock Key: '${lockKey}' and Claim: '${lockClaim}'`);
  return new Promise(async (resolve, reject) => {
    //  Peek at the Active Lock Claim
    let activeLockClaim = (0, exports.peekLock)(lockKey);
    console.log(`Current Lock Claim: '${activeLockClaim}'`);
    const handleLockTaken = async () => {
      console.log(`Lock Taken Error:  Another Claim has been made '${activeLockClaim}', so the '${lockClaim}' claim could not be established.`);
      if (hold) {
        console.log(`Waiting for the claim to expire...'`);
        await (0, exports.waitForUnlock)(lockKey);
      }
      console.log(`Rejecting with LockTakenError.'`);
      reject(new LockTakenError());
    };
    //  If Locked, then Reject
    if (!!activeLockClaim) {
      handleLockTaken();
      return;
    }
    //  If Unlocked...
    if (!activeLockClaim) {
      console.log(`The Lock is Free!'`);
      (0, exports.claimLock)(lockKey, lockClaim);
      console.log(`Tentatively Claimed the Lock.  Verifying Claim...'`);
      //  Verify The Claim
      //  NOTE:  Lock consumers may perform a simultaneous read on the empty lock.
      //         This is similar to a Write-After-Read Hazard in concurrency control.  
      //         To solve this, we read again after the write.
      await wait100();
      activeLockClaim = (0, exports.peekLock)(lockKey);
      const wonWriteRace = lockClaim === activeLockClaim;
      if (!activeLockClaim) {
        console.log(`Lock Could Not be Set: This is likely a cookie related issue.  Make sure the cookie can be set.'`);
        reject(new LockTakenError());
        return;
      }
      //  Resolve if we Won the Race
      if (wonWriteRace) {
        console.log(`Lock Claim has been Verified for this Claim: '${lockClaim}'`);
        resolve();
        return;
        //  Reject if we Lost the Race
      } else {
        handleLockTaken();
        return;
      }
    }
  });
};
exports.acquireCookieLock = acquireCookieLock;